import React, { useMemo } from 'react';
import { AgGridReact, CustomCellRendererProps } from 'ag-grid-react';
import { ColDef, themeAlpine } from 'ag-grid-community';
import { convertTimeHumanReadable } from '../../../DateTimeCell/DateTimeCell';
import { Log } from '../LogType';
import styles from './LogTable.module.scss';

const styled: React.CSSProperties = {
  fontFamily: 'Fira Sans,Helvetica Neue,Helvetica,Roboto,Arial,sans-serif',
};

interface LogTableProperties {
  logs: Log[];
}

const LogTable = ({ logs }: LogTableProperties) => {
  const columnDefinitions = useMemo<ColDef<Log>[]>(
    () => [
      {
        field: 'metadata.CSBWorkflowStepName',
        filter: 'agTextColumnFilter',
        filterParams: {
          buttons: ['cancel', 'reset', 'apply'],
          closeOnApply: true,
          maxNumConditions: 1,
          suppressAndOrCondition: true,
        },
        headerName: 'Step Name',
        valueGetter: (parameters) =>
          parameters.data?.metadata?.CSBWorkflowStepName ||
          'No Step name available',
      },
      {
        field: 'timestamp',
        filter: 'agDateColumnFilter',
        filterParams: {
          buttons: ['cancel', 'reset', 'apply'],
          closeOnApply: true,
          maxNumConditions: 1,
          suppressAndOrCondition: true,
        },
        headerName: 'Created time',
        maxWidth: 175,
        minWidth: 155,
        resizable: false,
        valueFormatter: (parameters) => {
          return convertTimeHumanReadable(parameters.value);
        },
      },
      {
        field: 'message',
        filter: 'agTextColumnFilter',
        filterParams: {
          buttons: ['cancel', 'reset', 'apply'],
          closeOnApply: true,
          maxNumConditions: 1,
          suppressAndOrCondition: true,
        },
        flex: 3,
        headerName: 'Message',
      },
      {
        cellRenderer: (parameters: CustomCellRendererProps<Log>) => {
          return (
            <span
              className={styles[parameters.value]}
              style={{
                color: 'white',
                padding: 5,
              }}
            >
              {parameters.value}
            </span>
          );
        },
        field: 'logLevel',
        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['cancel', 'reset', 'apply'],
          closeOnApply: true,
          maxNumConditions: 1,
          suppressAndOrCondition: true,
        },
        headerName: 'Severity',
        maxWidth: 120,
        minWidth: 120,
        resizable: false,
      },
    ],
    []
  );

  const defaultColumnDefinition = useMemo<ColDef>(
    () => ({
      filter: true,
      flex: 1,
      resizable: true,
      sortable: true,
      suppressHeaderMenuButton: true,
    }),
    []
  );

  return (
    <div className={styles.container}>
      <AgGridReact
        theme={themeAlpine.withParams(styled)}
        rowData={logs}
        columnDefs={columnDefinitions}
        defaultColDef={defaultColumnDefinition}
      />
    </div>
  );
};

export default LogTable;
